// === Admin: Pricelist Templates ===
// Full CRUD page — list, add, edit, soft-delete with inline bullet points.
// Ported from Click2Gro PHP (PricelistTemplatesController.php / pricelist_template table)
// Fields: plan_name, price, description, background_color + bullet_points (child rows)

import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  AttachMoney as MoneyIcon,
  FiberManualRecord as BulletIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  DEFAULT_ROWS_PER_PAGE,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface PricelistTemplateRow {
  readonly id: number;
  readonly plan_name: string | null;
  readonly description: string | null;
  readonly price: number | null;
  readonly background_color: string | null;
  readonly bullet_count: number;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

interface BulletPoint {
  readonly id: number;
  readonly bullet_point: string;
}

interface FormState {
  plan_name: string;
  description: string;
  price: string;
  background_color: string;
}

const EMPTY_FORM: FormState = {
  plan_name: '',
  description: '',
  price: '',
  background_color: '#ffffff',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const fmtPrice = (v: number | null) =>
  v != null ? `$${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(d);
}

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'https://api.click2gro.com.au';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export function PricelistTemplatesPage() {
  const navigate              = useNavigate();
  const { session }           = useAuth();
  const token                 = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows]           = useState<readonly PricelistTemplateRow[]>([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch]       = useState('');
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Form / dialog state ----
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editingId, setEditingId]     = useState<number | null>(null);
  const [form, setForm]               = useState<FormState>(EMPTY_FORM);
  const [bulletPoints, setBulletPoints] = useState<readonly BulletPoint[]>([]);
  const [newBullet, setNewBullet]     = useState('');
  const [pendingBullets, setPendingBullets] = useState<string[]>([]);
  const [formError, setFormError]     = useState<string | null>(null);
  const [saving, setSaving]           = useState(false);
  const [auditInfo, setAuditInfo]     = useState<{ created_at: string | null; updated_at: string | null; created_by: string | null; updated_by: string | null } | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId]       = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting]       = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // Search debounce
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 300);
  }, [search]);

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const params = new URLSearchParams({ page: String(page + 1), limit: String(rowsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const res = await fetch(`${getApiUrl()}/api/v1/admin/pricelist-templates?${params.toString()}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: PricelistTemplateRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Open Add
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setBulletPoints([]);
    setPendingBullets([]);
    setNewBullet('');
    setFormError(null);
    setAuditInfo(null);
    setDialogOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Open Edit
  // ---------------------------------------------------------------------------
  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/pricelist-templates/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: PricelistTemplateRow & { bullet_points: BulletPoint[]; created_by?: string; updated_by?: string } };
      const r = json.data;
      setForm({
        plan_name:        r.plan_name ?? '',
        description:      r.description ?? '',
        price:            r.price != null ? String(r.price) : '',
        background_color: r.background_color ?? '#ffffff',
      });
      setBulletPoints(r.bullet_points ?? []);
      setPendingBullets([]);
      setNewBullet('');
      setAuditInfo({
        created_at: r.created_at ?? null,
        updated_at: r.updated_at ?? null,
        created_by: r.created_by ?? null,
        updated_by: r.updated_by ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Bullet point helpers
  // ---------------------------------------------------------------------------
  const addBullet = () => {
    const trimmed = newBullet.trim();
    if (!trimmed) return;
    setPendingBullets((prev) => [...prev, trimmed]);
    setNewBullet('');
  };

  const removeSavedBullet = (bp: BulletPoint) => {
    setBulletPoints((prev) => prev.filter((b) => b.id !== bp.id));
  };

  const removePendingBullet = (idx: number) => {
    setPendingBullets((prev) => prev.filter((_, i) => i !== idx));
  };

  // All bullet point strings to be saved
  const allBullets = [...bulletPoints.map((b) => b.bullet_point), ...pendingBullets];

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    if (!token) return;
    setFormError(null);
    setSaving(true);
    try {
      const payload = {
        plan_name:        form.plan_name.trim(),
        description:      form.description.trim() || null,
        price:            form.price !== '' ? Number(form.price) : null,
        background_color: form.background_color !== '#ffffff' && form.background_color !== '' ? form.background_color : null,
        bullet_points:    allBullets,
      };

      const url    = editingId
        ? `${getApiUrl()}/api/v1/admin/pricelist-templates/${editingId}`
        : `${getApiUrl()}/api/v1/admin/pricelist-templates`;
      const method = editingId ? 'PATCH' : 'POST';

      const res = await fetch(url, { method, headers: authHeaders(token), body: JSON.stringify(payload) });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Record updated.' : 'Record added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/pricelist-templates/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      setDeleteLabel('');
      showSnack('Record deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Pricelist Templates</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage subscription pricelist plans and bullet points
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add New
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search plan name or description…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 320 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Plan Name</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 120 }}>Price</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 140 }}>Bullet Points</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No records found.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <AppTableRow key={row.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {row.background_color && (
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              backgroundColor: row.background_color,
                              border: '1px solid rgba(0,0,0,0.2)',
                              flexShrink: 0,
                            }}
                          />
                        )}
                        <Typography variant="body2" fontWeight={500}>{row.plan_name ?? '—'}</Typography>
                      </Box>
                      {row.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {row.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        {fmtPrice(row.price)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.bullet_count > 0 ? (
                        <Chip label={`${row.bullet_count} point${row.bullet_count > 1 ? 's' : ''}`} size="small" variant="outlined" />
                      ) : (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => void openEdit(row.id)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => confirmDelete(row.id, row.plan_name ?? String(row.id))}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </AppTableRow>
                ))
              )}
            </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId !== null ? 'Edit Pricelist Template' : 'Add Pricelist Template'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}

            {/* Row 1: Plan Name + Price */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Plan Name *"
                size="small"
                sx={{ flex: 2, minWidth: 220 }}
                value={form.plan_name}
                onChange={(e) => setForm((f) => ({ ...f, plan_name: e.target.value }))}
              />
              <TextField
                label="Price"
                size="small"
                sx={{ flex: 1, minWidth: 130 }}
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" /></InputAdornment>,
                }}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Box>

            {/* Row 2: Description */}
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              rows={4}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />

            {/* Row 3: Background Color */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>Background Color</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input
                  type="color"
                  value={form.background_color || '#ffffff'}
                  onChange={(e) => setForm((f) => ({ ...f, background_color: e.target.value }))}
                  style={{ width: 48, height: 36, padding: 2, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer' }}
                />
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{form.background_color}</Typography>
                <Button size="small" variant="text" onClick={() => setForm((f) => ({ ...f, background_color: '#ffffff' }))}>Reset</Button>
              </Box>
            </Box>

            {/* Row 4: Bullet Points */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <BulletIcon fontSize="small" color="primary" /> Bullet Points
              </Typography>

              {/* Existing + pending bullets */}
              {(bulletPoints.length > 0 || pendingBullets.length > 0) && (
                <List dense disablePadding sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  {bulletPoints.map((bp) => (
                    <ListItem
                      key={bp.id}
                      sx={{ py: 0.5 }}
                      secondaryAction={
                        <IconButton edge="end" size="small" color="error" onClick={() => removeSavedBullet(bp)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText primary={bp.bullet_point} primaryTypographyProps={{ variant: 'body2' }} />
                    </ListItem>
                  ))}
                  {pendingBullets.map((bp, idx) => (
                    <ListItem
                      key={`pending-${idx}`}
                      sx={{ py: 0.5, bgcolor: 'action.hover' }}
                      secondaryAction={
                        <IconButton edge="end" size="small" color="error" onClick={() => removePendingBullet(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={bp}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondary="new"
                        secondaryTypographyProps={{ variant: 'caption', color: 'success.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {/* Add bullet input */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Enter bullet point…"
                  value={newBullet}
                  onChange={(e) => setNewBullet(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBullet(); } }}
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 200 }}
                />
                <Button variant="outlined" size="small" onClick={addBullet} disabled={!newBullet.trim()}>
                  Add
                </Button>
              </Box>
            </Box>

            {/* Row 5: Audit info (edit only) */}
            {editingId !== null && auditInfo !== null && (
              <Box
                mt={1}
                p={2}
                sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                  Audit Trail
                </Typography>
                <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving || !form.plan_name.trim()}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {editingId !== null ? 'Save Changes' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirm Dialog                                                */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Pricelist Template?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteLabel}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteId(null); setDeleteLabel(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => void handleDelete()} disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
